import { BaseCrud } from '@/shared/models/cruds.models'
import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import { GetContextMenuItemsParams, MenuItemDef, RowNode } from '@ag-grid-enterprise/all-modules'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import IncidentsSeveritiesModal
    from '@/modules/configurationModule/detail/incidents/incidents-severities/components/incidents-severities-modal/incidents-severities-modal'
import IncidentsSeveritiesModalComponent
    from '@/modules/configurationModule/detail/incidents/incidents-severities/components/incidents-severities-modal/incidents-severities-modal.vue'
import {
    IncidentsSeveritiesGridState,
    initialIncidentsSeveritiesGridState
} from '@/modules/configurationModule/detail/incidents/incidents-severities/components/incidents-severities-grid/incidents-severities-grid.model'
import { incidentsSeveritiesLaboratoriesConfigurationServices } from '@/modules/configurationModule/detail/incidents/incidents-severities/services/incidents-severities-laboratories.service'
import {
    ConfigurationIncidentsSeveritiesLaboratoriesCreateRequest,
    ConfigurationIncidentsSeveritiesLaboratoriesPatchInBatchRequest,
    ConfigurationIncidentsSeveritiesLaboratoriesResponse
} from '@/modules/configurationModule/detail/incidents/incidents-severities/services/incidents-severities-laboratories.dto'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'
import { BModal } from 'bootstrap-vue'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        IncidentsSeveritiesModalComponent
    }
})
export default class IncidentsSeveritiesGrid extends BaseCrudServerSideComponent<ConfigurationIncidentsSeveritiesLaboratoriesResponse> implements BaseCrud {
    @Ref() readonly incidentsSeveritiesModalWrapper!: BModal
    @Ref() readonly incidentsSeveritiesModal!: IncidentsSeveritiesModal

    state: IncidentsSeveritiesGridState
    constructor() {
        super()
        this.state = initialIncidentsSeveritiesGridState()
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    get canUpdate(): boolean {
        const numberRowSelected: number = this.state.selectedRows.length
        return numberRowSelected === 1
    }

    private _showModalUpdateAction(): void {
        this.showModal(this.$t('configuration.actions.update').toString(), this.state.selectedRows[0], this.incidentsSeveritiesModalWrapper)
    }

    private _showModalCreateAction(): void {
        this.showModal(this.$t('configuration.actions.add').toString(), null, this.incidentsSeveritiesModalWrapper)
    }

    private _onPressShortKeyUpdateButton(): void {
        this._showModalUpdateAction()
    }

    private _onClickUpdateButton(): void {
        this._showModalUpdateAction()
    }

    private _onClickCreateButton(): void {
        this._showModalCreateAction()
    }

    private _onPressShortKeyCreateButton(): void {
        this._showModalCreateAction()
    }

    getContextMenuItems(params:GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$t('configuration.actions.update').toString(),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => {
                    const nodeSelected:RowNode = params?.node as RowNode
                    const selectedRow: ConfigurationIncidentsSeveritiesLaboratoriesResponse = nodeSelected?.data
                    return this.showModal(this.$t('configuration.actions.update') as string, selectedRow, this.incidentsSeveritiesModalWrapper)
                }
            },
            {
                name: this.$t('configuration.actions.delete').toString(),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: () => this.deleteOperation(this.state.selectedRows)
            },
            {
                name: params?.node?.data?.active ? this.$t('configuration.actions.deactivate').toString() : this.$t('configuration.actions.activate').toString(),
                cssClasses: this.state.selectedRows.length ? ['not-show'] : [''],
                action: () => this.activateDeactivateSelection(!params?.node?.data?.active, [params?.node?.data])
            },
            {
                name: this.$t('configuration.actions.activate').toString(),
                cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                action: () => this.activateDeactivateSelection(true, this.state.selectedRows)
            },
            {
                name: this.$t('configuration.actions.deactivate').toString(),
                cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                action: () => this.activateDeactivateSelection(false, this.state.selectedRows)
            }
        ]
    }

    async activateDeactivateSelection(option: boolean, selectionBatch: ConfigurationIncidentsSeveritiesLaboratoriesResponse[]): Promise<void> {
        const filteredRowActiveState = selectionBatch?.filter((row: ConfigurationIncidentsSeveritiesLaboratoriesResponse) => row.active !== option)
        const updatedFilteredRowActiveState: ConfigurationIncidentsSeveritiesLaboratoriesPatchInBatchRequest[] = filteredRowActiveState?.map((row: ConfigurationIncidentsSeveritiesLaboratoriesResponse) => {
            return {
                id: row.id,
                active: option
            }
        })
        if (updatedFilteredRowActiveState?.length) {
            this.$_updateLoading(true)
            const [err] = await this.$_toCall(incidentsSeveritiesLaboratoriesConfigurationServices.patchIncidentsSeveritiesLaboratory(this.state.labId, updatedFilteredRowActiveState), true)
            this.$_updateLoading(false)
            if (err) {
                return
            }
            if (selectionBatch.length > 1) {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 2, { elements: selectionBatch.length }))
            } else {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
            }
            this.reloadData()
        }
    }

    async createOperation(row: ConfigurationIncidentsSeveritiesLaboratoriesCreateRequest): Promise<void> {
        this.$_updateLoading(true)
        const [err] = await this.$_toCall(incidentsSeveritiesLaboratoriesConfigurationServices.createIncidentsSeveritiesLaboratory(this.state.labId, row), true)
        this.$_updateLoading(false)
        if (err) {
            return
        }
        this.$_showToastSuccess(this.$t('configuration.toastMessages.created')?.toString())
        this.reloadData()
    }

    async patchOperation(rows: ConfigurationIncidentsSeveritiesLaboratoriesPatchInBatchRequest[]): Promise<void> {
        this.$_updateLoading(true)
        const [err] = await this.$_toCall(incidentsSeveritiesLaboratoriesConfigurationServices.patchIncidentsSeveritiesLaboratory(this.state.labId, rows), true)
        this.$_updateLoading(false)
        if (err) {
            return
        }
        this.incidentsSeveritiesModalWrapper.hide()
        this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
        this.reloadData()
    }

    async deleteOperation(rows: ConfigurationIncidentsSeveritiesLaboratoriesResponse[]): Promise<void> {
        if (rows.length) {
            const selectedRowsSize:number = this.state?.selectedRows?.length
            const labelConfirmation:string = this.$t('configuration.grid.deleteConfirmation')?.toString()
            const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
            const labelDeleteQuestion:string = this.$t('configuration.grid.deleteQuestion')?.toString()
            const labelDeleteAction:string = this.$t('configuration.actions.delete')?.toString()
            const promise = await this.$_showConfirmationModal(
                `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelDeleteQuestion}`,
                labelDeleteAction
            )
            if (promise) {
                this._deleteBatchOperation(rows)
            }
        }
    }

    private async _deleteBatchOperation(rows: ConfigurationIncidentsSeveritiesLaboratoriesResponse[]): Promise<void> {
        const rowsToDelete: EntityIdentifiersRequest = {
            entityIdentifiers: rows.map((incidentSeverity: ConfigurationIncidentsSeveritiesLaboratoriesResponse) => Number(incidentSeverity?.id))
        }
        this.$_updateLoading(true)
        const [err] = await this.$_toCall(incidentsSeveritiesLaboratoriesConfigurationServices.deleteIncidentsSeveritiesLaboratory(this.state.labId, rowsToDelete), true)
        this.$_updateLoading(false)
        if (err) {
            return
        }
        if (rows.length > 1) {
            this.$_showToastSuccess(
                this.$tc('configuration.toastMessages.deleted', 2, { elements: rows?.length })
            )
        } else {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', 1))
        }
        this.reloadData()
    }
}
